import { useRef, useEffect } from 'react';
import global from './global';
import { KWH_UNIT, KWH_DECIMAL_POINT, KW_UNIT, KW_DECIMAL_POINT, NUM_FEET_IN_ONE_METER, NUM_SQUARE_FEET_IN_ONE_SQUARE_METER, ONE_MILLION, ONE_BILLION, ONE_TRILLION, } from './constants';
import { AreaUnitLabels, MapMeasurementSystemToAreaUnit, } from '../types/Global';
// renamed for clarity
export const getMapFromArrayOneToOne = (data, selector = 'id') => data.reduce((result, record) => {
    const key = record[selector];
    return Object.assign(result, { [key]: record }, {});
}, {});
/** @deprecated use getMapFromArrayOneToOne instead */
export const mapArrayToObject = getMapFromArrayOneToOne;
export const getMapFromArrayOneToMany = (data, selector = 'id') => data.reduce((result, record) => {
    const key = record[selector];
    let list = result[key];
    if (list === undefined)
        list = [];
    return Object.assign(result, { [key]: [...list, record] }, {});
}, {});
export const sumKeys = (object) => !object ? 0 : Object.keys(object).reduce((acc, key) => acc + object[key], 0);
// Map all values of the object with given function
export const mapValues = (data, f) => Object.keys(data).map((i) => f(data[i]));
// Returns values of given object
export const values = (obj) => Object.keys(obj).map((i) => obj[i]);
export const hasSubstr = (s, q) => (s || '').toLowerCase().indexOf((q || '').toLowerCase()) !== -1;
export const hasKeys = (o) => Object.keys(o || {}).length > 0;
// from https://flyingsky.github.io/2018/01/26/javascript-detect-chinese-japanese/
export const REGEX_CHINESE = /[\u4e00-\u9fff]|[\u3400-\u4dbf]|[\u{20000}-\u{2a6df}]|[\u{2a700}-\u{2b73f}]|[\u{2b740}-\u{2b81f}]|[\u{2b820}-\u{2ceaf}]|[\uf900-\ufaff]|[\u3300-\u33ff]|[\ufe30-\ufe4f]|[\uf900-\ufaff]|[\u{2f800}-\u{2fa1f}]/u;
export const shortenString = (string, length, ellipsis = '...') => {
    if (!isDefined(string))
        return '';
    const hasChinese = REGEX_CHINESE.test(string);
    if (hasChinese) {
        // Chinese characters are counted as 2 letters here, for the sake of more accurate space estimation
        let charCount = 0, lastCharIndex = 0;
        const keepWholeString = [...string].every((char, index) => {
            if (REGEX_CHINESE.test(char)) {
                charCount += 2;
            }
            else {
                charCount++;
            }
            if (charCount <= length) {
                lastCharIndex = index;
                return true;
            }
            else {
                return false;
            }
        });
        // Need to subtract 2 to account for ellipsis added, if ellipsis is ...
        return keepWholeString
            ? string
            : `${string.substring(0, lastCharIndex + 1 - Math.ceil(ellipsis.length / 2))}${ellipsis}`;
    }
    if (string.length <= length)
        return string;
    // Need to subtract 3 to account for ellipsis added, if ellipsis is ...
    return `${string.substring(0, length - ellipsis.length)}${ellipsis}`;
};
export function formatNumberString(value, options = {}) {
    const { unit = '', displayUnit = true, spaceBetweenValueAndUnit = true, countryCode = '', numDecimals, useNotAvailableText = false, } = options;
    if (!isDefined(value))
        return useNotAvailableText ? global.NOT_AVAILABLE : '';
    let absoluteValue = Math.abs(value);
    let result;
    const valueInMillions = Math.round(value / ONE_MILLION);
    const absoluteValueInMillions = Math.abs(valueInMillions);
    const valueInBillions = Math.round(value / ONE_BILLION);
    const absoluteValueInBillions = Math.abs(valueInBillions);
    const spaceIfNeeded = spaceBetweenValueAndUnit ? ' ' : '';
    if (absoluteValue < ONE_MILLION || unit === KWH_UNIT) {
        result = `${toLocaleString(value, countryCode, numDecimals)}${spaceIfNeeded}${displayUnit ? unit : ''}`;
    }
    else if (absoluteValue < ONE_BILLION && absoluteValueInMillions < 1000) {
        let millionsAbbreviation;
        if (countryCode === 'ID') {
            millionsAbbreviation = 'JT';
        }
        else {
            millionsAbbreviation = 'M';
        }
        result = `${valueInMillions.toFixed()} ${millionsAbbreviation}${spaceIfNeeded}${displayUnit ? unit : ''}`;
    }
    else if (absoluteValue < ONE_TRILLION && absoluteValueInBillions < 1000) {
        let billionsAbbreviation;
        if (countryCode === 'ID') {
            billionsAbbreviation = 'M';
        }
        else {
            billionsAbbreviation = 'B';
        }
        result = `${valueInBillions.toFixed()} ${billionsAbbreviation}${spaceIfNeeded}${displayUnit ? unit : ''}`;
    }
    else {
        result = `${value.toExponential(1)}${spaceIfNeeded}${displayUnit ? unit : ''}`;
    }
    return stripAndCollapseAllSpaces(result);
}
function toLocaleString(value, countryCode, numDecimals) {
    let options;
    if (numDecimals !== undefined) {
        options = {
            minimumFractionDigits: numDecimals,
            maximumFractionDigits: numDecimals,
        };
    }
    else {
        options = {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        };
    }
    if (value === null || value === undefined)
        return '';
    switch (countryCode) {
        case 'US':
            return value.toLocaleString('en-US', options);
        case 'ID':
            return value.toLocaleString('id', options);
        case 'HK':
            return value.toLocaleString('zh-HK', options);
        case 'CN':
            return value.toLocaleString('zh-CN', options);
        case 'MX':
            return value.toLocaleString('es-MX', options);
        case 'DE':
            return value.toLocaleString('de-DE', options);
        case 'PH':
            return value.toLocaleString('en-PH', options);
        default:
            return value.toLocaleString('en-US', options);
    }
}
export function stripAndCollapseAllSpaces(str) {
    return str.replace(/\s+/g, ' ').trim();
}
const determineLanguageFormat = (currencyCode, countryCode) => {
    switch (currencyCode) {
        case 'MUR':
            return 'en-MU';
        case 'IDR':
            return 'id-ID';
        default:
            return countryCode || 'en';
    }
};
export function formatCurrency(value, currencyCode, countryCode) {
    if (value === null || value === undefined || currencyCode === undefined)
        return global.NOT_AVAILABLE;
    return (Intl.NumberFormat(determineLanguageFormat(currencyCode, countryCode), {
        style: 'currency',
        currency: currencyCode,
    })
        .format(value)
        //replace non-breaking white space with conventional space,
        //required for proper testing in formatters.spec.js
        .replace(/\s/g, ' '));
}
//a bit of a hacky way of converting currencyCode to currencySymbol
//welcome to feedback for cleaner conversion
export const formatCurrencySymbol = (currencyCode, countryCode) => {
    const a = 0;
    return (a
        .toLocaleString(determineLanguageFormat(currencyCode, countryCode), {
        style: 'currency',
        currency: currencyCode,
    })
        .replace('0.00', '')
        .replace('0,00', '')
        //replace non-breaking white space, required for proper testing in formatters.spec.js
        .replace(/\s/g, ''));
};
export const formatKwhValues = (value, countryCode) => {
    return formatNumberString(value, {
        unit: KWH_UNIT,
        numDecimals: KWH_DECIMAL_POINT,
        countryCode: countryCode || '',
        useNotAvailableText: true,
    });
};
export const formatKwValues = (value, countryCode) => {
    return formatNumberString(value, {
        unit: KW_UNIT,
        numDecimals: KW_DECIMAL_POINT,
        countryCode,
    });
};
export const firstLetterAllWordsToUpper = (str) => {
    return str.replace(/(^\w|\s\w)/g, (m) => m.toUpperCase());
};
export function capitalizeFirstLetter([first, ...rest]) {
    if (first === undefined)
        return '';
    return [first.toUpperCase(), ...rest].join('');
}
export function capitalizeFirstLetterOnly(str) {
    if (str === undefined || str === '')
        return '';
    return capitalizeFirstLetter(str.toLowerCase());
}
export function toMultipleLines(title, maxNumLines, maxCharactersPerLine) {
    const lines = [[]];
    const splitTitle = title.split(' ');
    splitTitle.forEach((word, index) => {
        const currentLine = lines[lines.length - 1];
        const currentLineLength = currentLine.join('').length;
        // Unless this will be first word of new line need to add 1 to account for
        // space between existing string and new word
        const currentLineWithNewWordLength = currentLineLength + word.length + (currentLine.length === 0 ? 0 : 1);
        currentLine.push(word);
        if (lines.length < maxNumLines &&
            splitTitle[index + 1] !== undefined &&
            currentLineWithNewWordLength + splitTitle[index + 1].length + 1 >
                maxCharactersPerLine) {
            lines.push([]);
        }
    });
    return lines.map((line) => line.join(' '));
}
export function formatLargeNumber(value) {
    const suffixes = ['', 'k', 'm', 'b', 't'];
    let suffixIndex = 0;
    let newValue = Number(value);
    if (newValue < 1000)
        return newValue.toString();
    while (newValue >= 1000) {
        newValue /= 1000;
        suffixIndex++;
    }
    newValue = newValue.toPrecision(2).replace('.0', '');
    newValue += suffixes[suffixIndex];
    return newValue;
}
export function getRandomInt(max) {
    return Math.floor(Math.random() * Math.floor(max));
}
export function sortAlphabetically(a, b) {
    return a.localeCompare(b);
}
export const widthInPixelsFromCssValue = (cssPropertyValue) => {
    const remIndex = cssPropertyValue.indexOf('rem');
    if (remIndex >= 0) {
        return parseFloat(cssPropertyValue.slice(0, remIndex)) * 16;
    }
    return NaN;
};
export function isDefined(value) {
    return value !== undefined && value !== null;
}
export const roundUpToAndDivisibleByN = (numToRound, maxToRoundUp) => {
    const roundedUp = Math.ceil(numToRound);
    const modResult = roundedUp % maxToRoundUp;
    if (modResult === 0) {
        return roundedUp;
    }
    else {
        const amountNeededToAdd = maxToRoundUp - modResult;
        return roundedUp + amountNeededToAdd;
    }
};
// FIXME: Super straight forward implementation of "pluralize" helper.
// Replace business logic if more sophisticated approach when required.
export const pluralize = (noun, count) => count === 1 ? noun : `${noun}s`;
export const convertFeetToMeters = (measurement, numDecimals) => {
    const inMeters = measurement / NUM_FEET_IN_ONE_METER;
    return Number.parseFloat(inMeters.toFixed(numDecimals));
};
export const convertMetersToFeet = (measurement, numDecimals) => {
    const inFeet = measurement * NUM_FEET_IN_ONE_METER;
    return Number.parseFloat(inFeet.toFixed(numDecimals));
};
export const convertSquareFeetToMeters = (measurement, numDecimals) => {
    const inMeters = measurement / NUM_SQUARE_FEET_IN_ONE_SQUARE_METER;
    return Number.parseFloat(inMeters.toFixed(numDecimals));
};
export const convertSquareMetersToFeet = (measurement, numDecimals) => {
    const inFeet = measurement * NUM_SQUARE_FEET_IN_ONE_SQUARE_METER;
    return Number.parseFloat(inFeet.toFixed(numDecimals));
};
export const getComputedStyleProperty = (name) => {
    return getComputedStyle(document.documentElement).getPropertyValue(name);
};
export const getHexColorWithAlpha = (hexCode, alpha) => {
    let opacity = 0;
    if (alpha >= 0 && alpha <= 1) {
        opacity = 255 * alpha;
    }
    if (alpha >= 0 && alpha <= 100) {
        opacity = 255 * (alpha / 100);
    }
    opacity = Math.floor(opacity);
    return `${hexCode}${opacity.toString(16)}`;
};
// Useful for debugging,  tracing props update (or any arbitary object)
export function useTraceUpdate(props) {
    const prev = useRef(props);
    useEffect(() => {
        const changedProps = Object.entries(props).reduce((ps, [k, v]) => {
            if (prev.current[k] !== v) {
                ps[k] = [prev.current[k], v];
            }
            return ps;
        }, {});
        if (Object.keys(changedProps).length > 0) {
            console.debug('Changed props:', changedProps);
        }
        prev.current = props;
    });
}
export const getUserPreferenceAreaUnit = (currentUser) => {
    const { measurementSystem, area } = currentUser;
    const unit = area
        ? AreaUnitLabels[area]
        : MapMeasurementSystemToAreaUnit[measurementSystem];
    return unit;
};
// only handles common case, don't use for things like beach -> beaches
export const checkCommonPlural = (entity, count) => {
    if (count > 1)
        return `${count} ${entity}s`;
    return `${count} ${entity}`;
};
// MUST PROVIDE TYPE PARAMETERS FOR THIS FUNCTION TO WORK
// THIS IS TO PREVENT SENDING INCORRECT QUERY PARAMETERS TO THE API
// https://stackoverflow.com/questions/56687668/a-way-to-disable-type-argument-inference-in-generics
export const formatAPIFilters = (filters) => {
    if (filters === undefined)
        return '';
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach((property) => {
        const filterValue = filters[property];
        if (filterValue === undefined || filterValue === null)
            return;
        if (Array.isArray(filterValue)) {
            if (filterValue.length > 0) {
                queryParams.set(property, filterValue.join(','));
            }
        }
        else
            queryParams.set(property, filterValue);
    });
    return queryParams.toString();
};
